import {
  collection,
  doc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  deleteDoc,
  updateDoc,
  where,
  Timestamp,
} from "firebase/firestore";
import { db } from "../firebase/config";
import AIService from "./AIService";
import {
  AppConstantsInputProfile,
  CompetitorScreenshotFeature,
  CreateImplementationPlanDTO,
  FeatureDetail,
  GenerateImplementationConfigFieldsDTO,
  ImplementationPlan,
} from "../types/implementation";
import { buildAppContextLines } from "../utils/appContextPrompt";

const PLANS_COLLECTION = "implementationPlans";

type AIRawPlan = {
  suggestedBestAppName?: string;
  coreFeature?:
    | {
        name?: string;
        details?: string;
        howItWorks?: string;
        successLooksLike?: string;
      }
    | string;
  complementaryFeatures?: Array<
    | {
        name?: string;
        details?: string;
        howItWorks?: string;
        successLooksLike?: string;
      }
    | string
  >;
  brandIdentity?: {
    primaryColor?: string;
    secondaryColor?: string;
  };
  designPrinciples?: string[];
  coreDemographic?: string;
  targetPlatforms?: string[];
  summary?: string;
  appConstantsProfile?: Partial<AppConstantsInputProfile>;
  competitorScreenshotFeatures?: Array<{
    screenshotLabel?: string;
    featureName?: string;
    featureDetails?: string;
    implementationNotes?: string;
    userFlow?: string;
  }>;
  implementationStarterPrompt?: string;
  firebaseConfigSnippet?: string;
};

type AIConfigFields = Partial<AppConstantsInputProfile>;

const CORE_BUILDER_TECH_STACK = `App runtime: React 18 + TypeScript + Vite (SPA), packaged for mobile with Capacitor 8 (iOS + Android).
UI layer: Tailwind CSS, Radix UI primitives, custom UI components, Lucide icons, Framer Motion animations.
Navigation/state/data: React Router, React Query, React Context/hooks.
Backend platform: Firebase (Auth, Firestore, Remote Config, Storage, Analytics, Crashlytics, Messaging) + Firebase Cloud Functions (Node/JS).
AI integration: Unified AI endpoint service supporting provider/model routing (OpenAI, Google/Gemini/Veo, Kling workloads).
Monetization/ads: RevenueCat subscriptions + AdMob integration.
Native plugins/features: Capacitor plugins for camera, notifications, share, device, network, preferences, etc., plus voice recorder support.
Testing/tooling: Vitest + Testing Library, ESLint, TypeScript type-check, patch-package.
Build/deploy: Vite web builds + Capacitor sync/run for native, Firebase Hosting/Functions deployment workflows.`;

const IMPLEMENTATION_SCHEMA = {
  type: "object",
  properties: {
    suggestedBestAppName: { type: "string" },
    coreFeature: {
      type: "object",
      properties: {
        name: { type: "string" },
        details: { type: "string" },
        howItWorks: { type: "string" },
        successLooksLike: { type: "string" },
      },
      required: ["name", "details", "howItWorks", "successLooksLike"],
    },
    complementaryFeatures: {
      type: "array",
      items: {
        type: "object",
        properties: {
          name: { type: "string" },
          details: { type: "string" },
          howItWorks: { type: "string" },
          successLooksLike: { type: "string" },
        },
        required: ["name", "details", "howItWorks", "successLooksLike"],
      },
      minItems: 5,
      maxItems: 5,
    },
    brandIdentity: {
      type: "object",
      properties: {
        primaryColor: { type: "string" },
        secondaryColor: { type: "string" },
      },
      required: ["primaryColor", "secondaryColor"],
    },
    designPrinciples: {
      type: "array",
      items: { type: "string" },
      minItems: 3,
      maxItems: 8,
    },
    coreDemographic: { type: "string" },
    targetPlatforms: {
      type: "array",
      items: { type: "string" },
      minItems: 2,
      maxItems: 2,
    },
    summary: { type: "string" },
    appConstantsProfile: {
      type: "object",
      properties: {
        appId: { type: "string" },
        appNameNative: { type: "string" },
        appNameDisplay: { type: "string" },
        tagline: { type: "string" },
        version: { type: "string" },
        privacyUrl: { type: "string" },
        termsUrl: { type: "string" },
        supportEmail: { type: "string" },
        appStoreUrl: { type: "string" },
        playStoreUrl: { type: "string" },
        authMode: { type: "string", enum: ["firebase", "frictionless_device_id"] },
        enableAccounts: { type: "boolean" },
        activeAppStyle: {
          type: "string",
          enum: [
            "productivity",
            "fitness",
            "finance",
            "wellness",
            "education",
            "travel",
            "food",
            "ecommerce",
            "social",
            "ai",
          ],
        },
        bottomNavStyle: {
          type: "string",
          enum: [
            "floating_fab",
            "pill",
            "minimal_line",
            "bubble_dock",
            "segmented",
          ],
        },
        monetizationStrategy: {
          type: "string",
          enum: ["subscription_2tier", "subscription_3tier", "iap_only"],
        },
        enableNotifications: { type: "boolean" },
        notificationDataSource: {
          oneOf: [
            { type: "string", enum: ["firestore", "local"] },
            {
              type: "array",
              items: { type: "string", enum: ["firestore", "local"] },
              minItems: 1,
              uniqueItems: true,
            },
          ],
        },
        enablePushNotifications: { type: "boolean" },
        enableAnalytics: { type: "boolean" },
        enablePaywall: { type: "boolean" },
        hardPaywallEnabled: { type: "boolean" },
        enableReports: { type: "boolean" },
        enableStreak: { type: "boolean" },
        revenueCatEntitlementId: { type: "string" },
        profileAuthLinkMode: { type: "string", enum: ["signup", "login"] },
        aiProvidersEnabled: { type: "boolean" },
        aiTextGeneration: { type: "string" },
        aiImageGeneration: { type: "string" },
        aiVideoGeneration: { type: "string" },
      },
    },
    competitorScreenshotFeatures: {
      type: "array",
      items: {
        type: "object",
        properties: {
          screenshotLabel: { type: "string" },
          featureName: { type: "string" },
          featureDetails: { type: "string" },
          implementationNotes: { type: "string" },
          userFlow: { type: "string" },
        },
        required: [
          "screenshotLabel",
          "featureName",
          "featureDetails",
          "implementationNotes",
          "userFlow",
        ],
      },
    },
    implementationStarterPrompt: { type: "string" },
  },
  required: [
    "suggestedBestAppName",
    "coreFeature",
    "complementaryFeatures",
    "brandIdentity",
    "designPrinciples",
    "coreDemographic",
    "targetPlatforms",
    "summary",
  ],
};

const APP_CONFIG_FIELD_SCHEMA = {
  type: "object",
  properties: {
    appId: { type: "string" },
    appNameNative: { type: "string" },
    appNameDisplay: { type: "string" },
    tagline: { type: "string" },
    version: { type: "string" },
    privacyUrl: { type: "string" },
    termsUrl: { type: "string" },
    supportEmail: { type: "string" },
    appStoreUrl: { type: "string" },
    playStoreUrl: { type: "string" },
    authMode: { type: "string", enum: ["firebase", "frictionless_device_id"] },
    enableAccounts: { type: "boolean" },
    activeAppStyle: {
      type: "string",
      enum: [
        "productivity",
        "fitness",
        "finance",
        "wellness",
        "education",
        "travel",
        "food",
        "ecommerce",
        "social",
        "ai",
      ],
    },
    bottomNavStyle: {
      type: "string",
      enum: ["floating_fab", "pill", "minimal_line", "bubble_dock", "segmented"],
    },
    monetizationStrategy: {
      type: "string",
      enum: ["subscription_2tier", "subscription_3tier", "iap_only"],
    },
    enableNotifications: { type: "boolean" },
    notificationDataSource: {
      oneOf: [
        { type: "string", enum: ["firestore", "local"] },
        {
          type: "array",
          items: { type: "string", enum: ["firestore", "local"] },
          minItems: 1,
          uniqueItems: true,
        },
      ],
    },
    enablePushNotifications: { type: "boolean" },
    enableAnalytics: { type: "boolean" },
    enablePaywall: { type: "boolean" },
    hardPaywallEnabled: { type: "boolean" },
    enableReports: { type: "boolean" },
    enableStreak: { type: "boolean" },
    revenueCatEntitlementId: { type: "string" },
    profileAuthLinkMode: { type: "string", enum: ["signup", "login"] },
    aiProvidersEnabled: { type: "boolean" },
    aiTextGeneration: { type: "string" },
    aiImageGeneration: { type: "string" },
    aiVideoGeneration: { type: "string" },
  },
};

const SCREENSHOT_FEATURE_SCHEMA = {
  type: "object",
  properties: {
    competitorScreenshotFeatures: {
      type: "array",
      items: {
        type: "object",
        properties: {
          screenshotLabel: { type: "string" },
          featureName: { type: "string" },
          featureDetails: { type: "string" },
          implementationNotes: { type: "string" },
          userFlow: { type: "string" },
        },
        required: [
          "screenshotLabel",
          "featureName",
          "featureDetails",
          "implementationNotes",
          "userFlow",
        ],
      },
    },
  },
  required: ["competitorScreenshotFeatures"],
};

export class ImplementationService {
  private static generateId(prefix: string): string {
    return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  }

  private static toDate(value: any): Date {
    if (value instanceof Timestamp) return value.toDate();
    if (value instanceof Date) return value;
    return new Date(value);
  }

  private static normalizeArray(value: unknown, max = 10): string[] {
    if (!Array.isArray(value)) return [];
    return value
      .map((item) => String(item || "").trim())
      .filter(Boolean)
      .slice(0, max);
  }

  private static resolveStableScreenshotAnalysisModel(model?: string): string | undefined {
    const normalized = String(model || "").trim().toLowerCase();
    if (!normalized) return undefined;
    if (normalized.startsWith("gpt") || normalized.startsWith("o1") || normalized.startsWith("o3") || normalized.startsWith("o4")) {
      return "gemini-2.5-flash";
    }
    if (normalized.startsWith("gemini-3-")) {
      return "gemini-2.5-flash";
    }
    return model;
  }

  static defaultAppConstantsProfile(
    data: Pick<
      CreateImplementationPlanDTO,
      | "appId"
      | "appName"
      | "description"
      | "category"
      | "storeUrl"
      | "androidStoreUrl"
      | "iosStoreUrl"
    >,
  ): AppConstantsInputProfile {
    const category = String(data.category || "").toLowerCase();
    const styleMap: Record<string, AppConstantsInputProfile["activeAppStyle"]> = {
      fitness: "fitness",
      finance: "finance",
      wellness: "wellness",
      education: "education",
      travel: "travel",
      food: "food",
      ecommerce: "ecommerce",
      social: "social",
      ai: "ai",
    };
    const activeStyle = styleMap[category] || "productivity";
    return {
      appId: data.appId || "",
      appNameNative: data.appName || "",
      appNameDisplay: data.appName || "",
      tagline: this.normalizeDescriptionSentence(
        data.description || "",
        `A smarter ${activeStyle} app for daily use`,
      ),
      version: "1.0.0",
      privacyUrl: "",
      termsUrl: "",
      supportEmail: "",
      appStoreUrl: data.iosStoreUrl || data.storeUrl || "",
      playStoreUrl: data.androidStoreUrl || data.storeUrl || "",
      authMode: "firebase",
      enableAccounts: true,
      activeAppStyle: activeStyle,
      bottomNavStyle: "pill",
      monetizationStrategy: "subscription_2tier",
      enableNotifications: true,
      notificationDataSource: ["firestore"],
      enablePushNotifications: true,
      enableAnalytics: true,
      enablePaywall: true,
      hardPaywallEnabled: false,
      enableReports: true,
      enableStreak: true,
      revenueCatEntitlementId: "pro",
      profileAuthLinkMode: "signup",
      aiProvidersEnabled: false,
      aiTextGeneration: "Gemini:gemini-2.5-flash-lite",
      aiImageGeneration: "OpenAI:gpt-image-2.5-flare",
      aiVideoGeneration: "Kling:kling-v2.6-std",
    };
  }

  private static normalizeAppConstantsProfile(
    input: Partial<AppConstantsInputProfile> | undefined,
    fallback: AppConstantsInputProfile,
  ): AppConstantsInputProfile {
    const raw = input && typeof input === "object" ? input : {};
    const yesNo = (value: unknown, defaultValue: boolean) =>
      typeof value === "boolean" ? value : defaultValue;
    const pick = <T extends string>(
      value: unknown,
      allowed: readonly T[],
      defaultValue: T,
    ): T => (allowed.includes(String(value || "") as T) ? (value as T) : defaultValue);
    const normalizeProviderModel = (
      value: unknown,
      fallbackValue: string,
    ): string => {
      const trimmed = String(value || "").trim();
      if (!trimmed) return fallbackValue;
      return trimmed.includes(":") ? trimmed : fallbackValue;
    };
    const normalizeNotificationDataSource = (
      value: unknown,
      fallbackValue: Array<"firestore" | "local">,
    ): Array<"firestore" | "local"> => {
      const allowed: Array<"firestore" | "local"> = ["firestore", "local"];
      if (Array.isArray(value)) {
        const normalized = value
          .map((item) => String(item || "").trim())
          .filter((item): item is "firestore" | "local" =>
            allowed.includes(item as "firestore" | "local"),
          );
        return normalized.length ? Array.from(new Set(normalized)) : fallbackValue;
      }
      if (typeof value === "string" && allowed.includes(value as any)) {
        return [value as "firestore" | "local"];
      }
      return fallbackValue.length ? fallbackValue : ["firestore"];
    };

    return {
      appId: String(raw.appId || fallback.appId),
      appNameNative: String(raw.appNameNative || fallback.appNameNative),
      appNameDisplay: String(raw.appNameDisplay || fallback.appNameDisplay),
      tagline: String(raw.tagline || fallback.tagline),
      version: String(raw.version || fallback.version),
      privacyUrl: String(raw.privacyUrl || fallback.privacyUrl),
      termsUrl: String(raw.termsUrl || fallback.termsUrl),
      supportEmail: String(raw.supportEmail || fallback.supportEmail),
      appStoreUrl: String(raw.appStoreUrl || fallback.appStoreUrl),
      playStoreUrl: String(raw.playStoreUrl || fallback.playStoreUrl),
      authMode: pick(raw.authMode, ["firebase", "frictionless_device_id"], fallback.authMode),
      enableAccounts: yesNo(raw.enableAccounts, fallback.enableAccounts),
      activeAppStyle: pick(
        raw.activeAppStyle,
        [
          "productivity",
          "fitness",
          "finance",
          "wellness",
          "education",
          "travel",
          "food",
          "ecommerce",
          "social",
          "ai",
        ],
        fallback.activeAppStyle,
      ),
      bottomNavStyle: pick(
        raw.bottomNavStyle,
        ["floating_fab", "pill", "minimal_line", "bubble_dock", "segmented"],
        fallback.bottomNavStyle,
      ),
      monetizationStrategy: pick(
        raw.monetizationStrategy,
        ["subscription_2tier", "subscription_3tier", "iap_only"],
        fallback.monetizationStrategy,
      ),
      enableNotifications: yesNo(raw.enableNotifications, fallback.enableNotifications),
      notificationDataSource: normalizeNotificationDataSource(
        raw.notificationDataSource,
        fallback.notificationDataSource,
      ),
      enablePushNotifications: yesNo(raw.enablePushNotifications, fallback.enablePushNotifications),
      enableAnalytics: yesNo(raw.enableAnalytics, fallback.enableAnalytics),
      enablePaywall: yesNo(raw.enablePaywall, fallback.enablePaywall),
      hardPaywallEnabled: yesNo(raw.hardPaywallEnabled, fallback.hardPaywallEnabled),
      enableReports: yesNo(raw.enableReports, fallback.enableReports),
      enableStreak: yesNo(raw.enableStreak, fallback.enableStreak),
      revenueCatEntitlementId: String(
        raw.revenueCatEntitlementId || fallback.revenueCatEntitlementId,
      ),
      profileAuthLinkMode: pick(
        raw.profileAuthLinkMode,
        ["signup", "login"],
        fallback.profileAuthLinkMode,
      ),
      aiProvidersEnabled: yesNo(raw.aiProvidersEnabled, fallback.aiProvidersEnabled),
      aiTextGeneration: normalizeProviderModel(
        raw.aiTextGeneration,
        fallback.aiTextGeneration,
      ),
      aiImageGeneration: normalizeProviderModel(
        raw.aiImageGeneration,
        fallback.aiImageGeneration,
      ),
      aiVideoGeneration: normalizeProviderModel(
        raw.aiVideoGeneration,
        fallback.aiVideoGeneration,
      ),
    };
  }

  private static normalizeCompetitorScreenshotFeatures(
    value: AIRawPlan["competitorScreenshotFeatures"],
  ): CompetitorScreenshotFeature[] {
    if (!Array.isArray(value)) return [];
    return value
      .map((item, index) => ({
        screenshotLabel: String(item?.screenshotLabel || `Screenshot ${index + 1}`),
        featureName: String(item?.featureName || "").trim(),
        featureDetails: String(item?.featureDetails || "").trim(),
        implementationNotes: String(item?.implementationNotes || "").trim(),
        userFlow: String(item?.userFlow || "").trim(),
      }))
      .filter((item) => item.featureName && item.featureDetails);
  }

  private static enrichCoreFeatureWithScreenshotContext(
    coreFeature: FeatureDetail,
    screenshotFeatures: CompetitorScreenshotFeature[],
  ): FeatureDetail {
    if (!screenshotFeatures.length) {
      return coreFeature;
    }
    const names = screenshotFeatures
      .map((item) => item.featureName)
      .filter(Boolean)
      .slice(0, 3);
    if (!names.length) {
      return coreFeature;
    }

    const haystack = [
      coreFeature.name,
      coreFeature.details,
      coreFeature.howItWorks,
      coreFeature.successLooksLike,
    ]
      .join(" ")
      .toLowerCase();
    const alreadyMentions = names.some((name) =>
      haystack.includes(String(name).toLowerCase()),
    );
    if (alreadyMentions) {
      return coreFeature;
    }

    const screenshotLine = `Informed by competitor screenshot analysis: ${names.join(", ")}.`;
    return {
      ...coreFeature,
      details: `${coreFeature.details} ${screenshotLine}`.trim(),
      howItWorks: `${coreFeature.howItWorks} Adapt the strongest interaction patterns from the analyzed screenshots while preserving this app's unique value.`.trim(),
    };
  }

  private static buildStarterPromptFallback(input: {
    appName: string;
    summary: string;
    coreFeature: FeatureDetail;
    complementaryFeatures: FeatureDetail[];
    screenshotFeatures: CompetitorScreenshotFeature[];
    appConstantsProfile: AppConstantsInputProfile;
  }): string {
    const constants = input.appConstantsProfile;
    return [
      `# ${input.appName} Implementation Starter Prompt`,
      "",
      "## Product Summary",
      input.summary,
      "",
      "## Core Feature",
      `- ${input.coreFeature.name}: ${input.coreFeature.details}`,
      `- How it works: ${input.coreFeature.howItWorks}`,
      `- Success KPI: ${input.coreFeature.successLooksLike}`,
      "",
      "## Complementary Features",
      ...input.complementaryFeatures.map(
        (feature, index) =>
          `${index + 1}. ${feature.name}: ${feature.details} | Flow: ${
            feature.howItWorks
          } | KPI: ${feature.successLooksLike}`,
      ),
      "",
      "## Competitor Screenshot Breakdown",
      ...(input.screenshotFeatures.length
        ? input.screenshotFeatures.map(
            (feature, index) =>
              `${index + 1}. [${feature.screenshotLabel}] ${feature.featureName}\n- Details: ${feature.featureDetails}\n- Implementation: ${feature.implementationNotes}\n- User flow: ${feature.userFlow}`,
          )
        : ["- No screenshots provided"]),
      "",
      "## APP CONSTANTS CONFIG (app.constants.ts source of truth)",
      "Use the following validated baseline:",
      `- APP_ID: ${constants.appId}`,
      `- APP_NAME: ${constants.appNameNative}`,
      `- APP_CONFIG.name: ${constants.appNameDisplay}`,
      `- APP_CONFIG.tagline: ${constants.tagline}`,
      `- APP_CONFIG.version: ${constants.version}`,
      `- APP_CONFIG.privacyUrl: ${constants.privacyUrl}`,
      `- APP_CONFIG.termsUrl: ${constants.termsUrl}`,
      `- APP_CONFIG.supportEmail: ${constants.supportEmail}`,
      `- APP_CONFIG.appStoreUrl: ${constants.appStoreUrl}`,
      `- APP_CONFIG.playStoreUrl: ${constants.playStoreUrl}`,
      `- APP_AUTH_MODE: ${constants.authMode}`,
      `- APP_CONFIG.enableAccounts: ${constants.enableAccounts}`,
      `- ACTIVE_APP_STYLE: ${constants.activeAppStyle}`,
      `- BOTTOM_NAV_CONFIG.activeStyle: ${constants.bottomNavStyle}`,
      `- MONETIZATION_STRATEGY: ${constants.monetizationStrategy}`,
      `- APP_CONFIG.enableNotifications: ${constants.enableNotifications}`,
      `- APP_CONFIG.notifications.dataSource: ${constants.notificationDataSource.join(", ")}`,
      `- APP_CONFIG.enablePushNotifications: ${constants.enablePushNotifications}`,
      `- APP_CONFIG.enableAnalytics: ${constants.enableAnalytics}`,
      `- APP_CONFIG.enablePaywall: ${constants.enablePaywall}`,
      `- APP_CONFIG.hardPaywallEnabled: ${constants.hardPaywallEnabled}`,
      `- APP_CONFIG.enableReports: ${constants.enableReports}`,
      `- APP_CONFIG.enableStreak: ${constants.enableStreak}`,
      `- REVENUECAT_ENTITLEMENT_ID: ${constants.revenueCatEntitlementId}`,
      `- APP_CONFIG.auth.profileAuthLinkMode: ${constants.profileAuthLinkMode}`,
      `- APP_CONFIG.aiProviders.enabled: ${constants.aiProvidersEnabled}`,
      `- REMOTE_CONFIG.ai_text_generation: ${constants.aiTextGeneration}`,
      `- REMOTE_CONFIG.ai_image_generation: ${constants.aiImageGeneration}`,
      `- REMOTE_CONFIG.ai_video_generation: ${constants.aiVideoGeneration}`,
      "",
      "## AI Provider Selection (Optional JSON block)",
      "```json",
      JSON.stringify(
        {
          aiProviders: {
            enabled: constants.aiProvidersEnabled,
            textGeneration: constants.aiTextGeneration,
            imageGeneration: constants.aiImageGeneration,
            videoGeneration: constants.aiVideoGeneration,
          },
        },
        null,
        2,
      ),
      "```",
      "",
      "## Build Instructions",
      "- Scaffold the app with Android and iOS support.",
      "- Implement feature modules in the order listed above.",
      "- Keep UX and interaction patterns aligned to screenshot breakdown.",
      "- Ensure constants are centralized in app.constants.ts and imported everywhere.",
      "",
      "## Core Builder Tech Stack",
      CORE_BUILDER_TECH_STACK,
    ].join("\n");
  }

  private static toFeatureDetail(
    value:
      | AIRawPlan["coreFeature"]
      | {
          name?: string;
          details?: string;
          howItWorks?: string;
          successLooksLike?: string;
        }
      | string
      | undefined,
    fallbackName: string,
    fallbackDetails: string,
  ): FeatureDetail {
    if (typeof value === "string") {
      const trimmed = value.trim();
      return {
        name: trimmed || fallbackName,
        details: fallbackDetails,
        howItWorks:
          "Guides users through a clear, focused flow to complete this outcome quickly.",
        successLooksLike:
          "Users repeatedly use this feature and complete the core action with low friction.",
      };
    }

    const name = String(value?.name || "").trim() || fallbackName;
    const details = String(value?.details || "").trim() || fallbackDetails;
    const howItWorks =
      String(value?.howItWorks || "").trim() ||
      "Guides users through a clear, focused flow to complete this outcome quickly.";
    const successLooksLike =
      String(value?.successLooksLike || "").trim() ||
      "Users repeatedly use this feature and complete the core action with low friction.";

    return { name, details, howItWorks, successLooksLike };
  }

  private static isGenericFeatureName(value: string): boolean {
    const v = value.toLowerCase();
    return (
      /^complementary feature \d+$/i.test(v) ||
      v === "core value delivery" ||
      v.includes("high-value core user outcome")
    );
  }

  private static isGenericFeatureBody(value: string): boolean {
    const v = value.toLowerCase();
    return (
      v.includes("adds practical support for") ||
      v.includes("guides users through a clear, focused flow") ||
      v.includes("introduces a lightweight workflow") ||
      v.includes("users repeatedly use this feature") ||
      v.includes("contributes to stronger weekly engagement")
    );
  }

  private static isLowQualityFeature(feature: FeatureDetail): boolean {
    return (
      this.isGenericFeatureName(feature.name) ||
      this.isGenericFeatureBody(feature.details) ||
      this.isGenericFeatureBody(feature.howItWorks) ||
      this.isGenericFeatureBody(feature.successLooksLike)
    );
  }

  private static normalizeDescriptionSentence(
    description: string,
    fallback: string,
  ): string {
    const raw = String(description || "").trim();
    if (!raw) return fallback;
    const sentence = raw
      .split(/[.!?]/)
      .map((item) => item.trim())
      .find(Boolean);
    return sentence || fallback;
  }

  private static buildContextualCoreFeature(
    data: CreateImplementationPlanDTO,
  ): FeatureDetail {
    const base = this.normalizeDescriptionSentence(
      data.description,
      `${data.appName} helps users achieve a clear, repeatable result in the ${data.category} space`,
    );
    return {
      name: `Core Value: ${base.slice(0, 70)}`,
      details: `Deliver the app's primary promise by turning "${base}" into a fast daily workflow with clear outputs and progress visibility.`,
      howItWorks:
        "User completes one primary input, the app generates a clear personalized result, then prompts the next best action to reinforce habit.",
      successLooksLike:
        "At least 40%+ of new users complete the primary flow in week 1 and return multiple times weekly to repeat the same value loop.",
    };
  }

  private static buildContextualComplementaryFeatures(
    data: CreateImplementationPlanDTO,
  ): FeatureDetail[] {
    const appName = data.appName;
    return [
      {
        name: "Smart Personalization Layer",
        details: `Tailor ${appName} outputs and guidance to user profile, goals, and recent behavior for higher relevance.`,
        howItWorks:
          "Collect onboarding preferences and behavior signals, then adapt recommendations, defaults, and notifications automatically.",
        successLooksLike:
          "Users engage with personalized recommendations significantly more than generic ones, improving retention and completion rates.",
      },
      {
        name: "Progress and Milestone Tracking",
        details: `Show users measurable improvement over time so ${appName} becomes outcome-driven rather than feature-driven.`,
        howItWorks:
          "Transform key actions into daily/weekly metrics, milestone badges, and trend visuals that make progress obvious.",
        successLooksLike:
          "Users check progress screens weekly and milestone completion correlates with stronger long-term retention.",
      },
      {
        name: "Actionable Reminder System",
        details: `Keep users consistent with context-aware reminders tied to the core flow of ${appName}.`,
        howItWorks:
          "Send reminders based on user routines and missed streaks, with one-tap deep links directly into the next required action.",
        successLooksLike:
          "Reminder-driven sessions convert into completed core actions at a healthy rate without causing notification fatigue.",
      },
      {
        name: "Guided Onboarding and Education",
        details: `Reduce time-to-value in ${appName} with concise guidance for first-time users.`,
        howItWorks:
          "Use an interactive onboarding checklist and just-in-time tips that appear only at meaningful moments.",
        successLooksLike:
          "New users complete onboarding quickly, and first-week churn decreases due to clearer product understanding.",
      },
      {
        name: "Feedback and Iteration Loop",
        details: `Capture user feedback inside ${appName} to continuously improve both the core feature and companion features.`,
        howItWorks:
          "Prompt micro-feedback after key flows and route recurring issues to prioritized product improvements.",
        successLooksLike:
          "Feedback response rates stay healthy and recurring friction themes decline across releases.",
      },
    ];
  }

  private static parseJson<T>(text: string): T | null {
    const raw = String(text || "").trim();
    if (!raw) return null;

    try {
      return JSON.parse(raw) as T;
    } catch {
      // continue
    }

    const fenced =
      raw.match(/```json\s*([\s\S]*?)\s*```/i)?.[1] ||
      raw.match(/```\s*([\s\S]*?)\s*```/i)?.[1];
    if (fenced) {
      try {
        return JSON.parse(fenced) as T;
      } catch {
        // continue
      }
    }

    const start = raw.indexOf("{");
    const end = raw.lastIndexOf("}");
    if (start >= 0 && end > start) {
      try {
        return JSON.parse(raw.slice(start, end + 1)) as T;
      } catch {
        return null;
      }
    }

    return null;
  }

  private static buildPrompt(data: CreateImplementationPlanDTO): string {
    const defaultProfile = this.defaultAppConstantsProfile(data);
    const normalizedProfile = this.normalizeAppConstantsProfile(
      data.appConstantsProfile,
      defaultProfile,
    );
    const screenshots = Array.isArray(data.competitorScreenshots)
      ? data.competitorScreenshots
      : [];
    const analyzedScreenshotFeatures = this.normalizeCompetitorScreenshotFeatures(
      data.competitorScreenshotFeatureDraft as AIRawPlan["competitorScreenshotFeatures"],
    );
    const firebaseConfigSnippet = String(data.firebaseConfigSnippet || "").trim();
    return [
      "You are a senior product strategist and app brand expert.",
      "Based on app context and competitor screenshots, generate an implementation expander report.",
      "",
      "App context:",
      ...buildAppContextLines({
        name: data.appName,
        description: data.description,
        category: data.category,
        platform: data.platform,
        storeUrl: data.storeUrl,
        androidStoreUrl: data.androidStoreUrl,
        iosStoreUrl: data.iosStoreUrl,
        researchContext: data.researchContext,
      }).map((line) => `- ${line}`),
      "",
      "App constants profile candidate (reviewed by user):",
      `- ${JSON.stringify(normalizedProfile)}`,
      "",
      "Core builder tech stack baseline to include in report context:",
      CORE_BUILDER_TECH_STACK,
      "",
      `Competitor screenshots attached: ${screenshots.length}`,
      ...screenshots.map(
        (item, index) =>
          `- Screenshot ${index + 1} label: ${item.name || `Screenshot ${index + 1}`}`,
      ),
      "",
      `Pre-analyzed screenshot feature drafts provided by user: ${analyzedScreenshotFeatures.length}`,
      ...analyzedScreenshotFeatures.map(
        (feature, index) =>
          `- Draft ${index + 1}: [${feature.screenshotLabel}] ${feature.featureName} | ${feature.featureDetails}`,
      ),
      "",
      "Firebase config snippet provided by user:",
      firebaseConfigSnippet ? firebaseConfigSnippet : "N/A",
      "",
      "Rules:",
      "- targetPlatforms must always include android and ios.",
      "- complementaryFeatures must have exactly 5 items.",
      "- Brand colors must be valid hex values.",
      "- For coreFeature and each complementary feature, provide concise but concrete details.",
      "- Explain how each feature works and what success looks like in measurable user behavior terms.",
      '- Never output placeholder feature names like "Core Value Delivery" or "Complementary Feature 1".',
      "- Do not repeat the same sentence across multiple features.",
      "- Keep outputs specific, practical, and market-aware.",
      '- Avoid generic placeholder language like "complementary feature 1" or "high-value outcome".',
      "- For each attached screenshot, identify at least one concrete feature and explain implementation notes and user flow.",
      "- If pre-analyzed screenshot features are provided, use them as source context and refine them (do not ignore).",
      "- IMPORTANT: coreFeature must explicitly incorporate insight from analyzed screenshots when screenshots are provided.",
      "- Build an implementationStarterPrompt that can be sent to an AI coding agent as the first prompt to scaffold and implement this app.",
      "- implementationStarterPrompt must include an APP CONSTANTS section aligned to the profile values and allowed enums.",
      "- Include AI provider routing when relevant using Provider:model-id format.",
      "- Populate Remote Config keys: ai_text_generation, ai_image_generation, ai_video_generation.",
      "- For video generation workloads, prioritize Kling models unless app context strongly requires otherwise.",
      '- AI provider fields must use Provider:model-id format (example: "Gemini:gemini-2.5-flash-lite").',
      "- If Firebase config snippet is provided, include a short implementation note on where it should be used.",
      "- Return valid JSON only with keys:",
      '{ "suggestedBestAppName": string, "coreFeature": { "name": string, "details": string, "howItWorks": string, "successLooksLike": string }, "complementaryFeatures": [{ "name": string, "details": string, "howItWorks": string, "successLooksLike": string } x5], "brandIdentity": { "primaryColor": string, "secondaryColor": string }, "designPrinciples": string[], "coreDemographic": string, "targetPlatforms": string[], "summary": string, "appConstantsProfile": object, "competitorScreenshotFeatures": [{ "screenshotLabel": string, "featureName": string, "featureDetails": string, "implementationNotes": string, "userFlow": string }], "implementationStarterPrompt": string }',
    ].join("\n");
  }

  private static isLowQualityOutput(parsed: AIRawPlan): boolean {
    const coreFeatureName =
      typeof parsed.coreFeature === "string"
        ? parsed.coreFeature
        : parsed.coreFeature?.name || "";
    const coreFeatureText = String(coreFeatureName || "").toLowerCase();
    const summary = String(parsed.summary || "").toLowerCase();
    const complementary = Array.isArray(parsed.complementaryFeatures)
      ? parsed.complementaryFeatures.map((item) =>
          String(typeof item === "string" ? item : item?.name || "")
            .trim()
            .toLowerCase(),
        )
      : [];

    const hasGenericCore =
      coreFeatureText.length === 0 ||
      coreFeatureText.includes("high-value core user outcome") ||
      coreFeatureText.includes("define and build");
    const hasGenericSummary =
      summary.length === 0 ||
      summary.includes("implementation expansion generated");
    const genericComplementaryCount = complementary.filter((item) =>
      /^complementary feature \d+$/i.test(item),
    ).length;

    const coreFeatureDetail = this.toFeatureDetail(
      parsed.coreFeature,
      "Core Value Delivery",
      "Define and build one high-value core user outcome.",
    );
    const complementaryFeatures =
      Array.isArray(parsed.complementaryFeatures) &&
      parsed.complementaryFeatures.length
        ? parsed.complementaryFeatures.map((item, index) =>
            this.toFeatureDetail(
              item,
              `Complementary Feature ${index + 1}`,
              "Adds practical support for core value delivery.",
            ),
          )
        : [];
    const lowQualityFeatureCount = complementaryFeatures.filter((feature) =>
      this.isLowQualityFeature(feature),
    ).length;

    return (
      hasGenericCore ||
      hasGenericSummary ||
      genericComplementaryCount >= 2 ||
      this.isLowQualityFeature(coreFeatureDetail) ||
      lowQualityFeatureCount >= 2
    );
  }

  private static async generatePlanJson(
    data: CreateImplementationPlanDTO,
    retryHint?: string,
  ): Promise<AIRawPlan> {
    const prompt = [
      this.buildPrompt(data),
      retryHint
        ? `\nQuality retry instruction: ${retryHint}\nRewrite with specific, market-aware outputs tied to this app context.`
        : "",
    ]
      .filter(Boolean)
      .join("\n");

    const hasReviewedScreenshotDrafts =
      Array.isArray(data.competitorScreenshotFeatureDraft) &&
      data.competitorScreenshotFeatureDraft.length > 0;
    const inputImages = hasReviewedScreenshotDrafts
      ? []
      : (data.competitorScreenshots || [])
          .slice(0, 4)
          .map((item) => ({
            dataUrl: item.dataUrl,
            mimeType: item.mimeType,
          }))
          .filter((item) => item.dataUrl);

    try {
      return await AIService.generateJSON<AIRawPlan>(prompt, {
        model: data.model || undefined,
        temperature: 0.2,
        maxTokens: 2048,
        structuredOutputName: "implementation_expander",
        structuredOutputSchema: IMPLEMENTATION_SCHEMA,
        inputImages,
      });
    } catch (primaryError) {
      try {
        const text = await AIService.generateText(prompt, {
          model: data.model || undefined,
          temperature: 0.2,
          maxTokens: 2048,
          inputImages,
        });
        return this.parseJson<AIRawPlan>(text.text) || {};
      } catch {
        const fallbackModel = AIService.getDefaultModel();
        if (fallbackModel === (data.model || "")) {
          throw primaryError;
        }
        return AIService.generateJSON<AIRawPlan>(prompt, {
          model: fallbackModel,
          temperature: 0.2,
          maxTokens: 2048,
          structuredOutputName: "implementation_expander",
          structuredOutputSchema: IMPLEMENTATION_SCHEMA,
          inputImages,
        });
      }
    }
  }

  static async generatePlan(
    userId: string,
    data: CreateImplementationPlanDTO,
  ): Promise<ImplementationPlan> {
    let parsed = await this.generatePlanJson(data);
    if (this.isLowQualityOutput(parsed)) {
      parsed = await this.generatePlanJson(
        data,
        "Previous output was too generic. Use concrete, app-specific feature names and unique implementation details.",
      );
    }
    if (this.isLowQualityOutput(parsed)) {
      parsed = await this.generatePlanJson(
        data,
        "Final retry: prohibit placeholders and repeated language; every feature must be directly tied to app description and category.",
      );
    }
    const targetPlatformsRaw = this.normalizeArray(
      parsed.targetPlatforms,
      4,
    ).map((item) => item.toLowerCase());
    const targetPlatforms: Array<"android" | "ios"> = ["android", "ios"].filter(
      (platform) => targetPlatformsRaw.includes(platform),
    ) as Array<"android" | "ios">;
    if (targetPlatforms.length < 2) {
      targetPlatforms.splice(0, targetPlatforms.length, "android", "ios");
    }

    const complementaryRaw = Array.isArray(parsed.complementaryFeatures)
      ? parsed.complementaryFeatures
      : [];
    let complementary: FeatureDetail[] = complementaryRaw
      .slice(0, 5)
      .map((item, index) =>
        this.toFeatureDetail(
          item,
          `Complementary Feature ${index + 1}`,
          `Adds practical support for ${data.appName}'s core value in day-to-day usage.`,
        ),
      );
    const fallbackComplementary =
      this.buildContextualComplementaryFeatures(data);
    complementary = complementary.map((feature, index) =>
      this.isLowQualityFeature(feature)
        ? fallbackComplementary[index]
        : feature,
    );
    while (complementary.length < 5) {
      complementary.push(fallbackComplementary[complementary.length]);
    }

    const normalizeHex = (value: string | undefined, fallback: string) =>
      /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(String(value || "").trim())
        ? String(value).trim()
        : fallback;

    const planId = doc(collection(db, PLANS_COLLECTION)).id;
    const defaultProfile = this.defaultAppConstantsProfile(data);
    const appConstantsProfile = this.normalizeAppConstantsProfile(
      parsed.appConstantsProfile || data.appConstantsProfile,
      defaultProfile,
    );
    const screenshotFeatures = this.normalizeCompetitorScreenshotFeatures(
      parsed.competitorScreenshotFeatures,
    );
    const rawCoreFeature = this.isLowQualityFeature(
      this.toFeatureDetail(
        parsed.coreFeature,
        "Core Value Delivery",
        `The primary value loop that makes ${data.appName} meaningfully useful to the target user.`,
      ),
    )
      ? this.buildContextualCoreFeature(data)
      : this.toFeatureDetail(
          parsed.coreFeature,
          "Core Value Delivery",
          `The primary value loop that makes ${data.appName} meaningfully useful to the target user.`,
        );
    const coreFeature = this.enrichCoreFeatureWithScreenshotContext(
      rawCoreFeature,
      screenshotFeatures,
    );
    const implementationStarterPrompt = String(
      parsed.implementationStarterPrompt || "",
    ).trim();
    const payload: ImplementationPlan = {
      planId,
      userId,
      appId: data.appId,
      appName: data.appName,
      selectedModel: data.model || AIService.getDefaultModel(),
      suggestedBestAppName:
        String(parsed.suggestedBestAppName || "").trim() || data.appName,
      coreFeature,
      complementaryFeatures: complementary.slice(0, 5),
      brandIdentity: {
        primaryColor: normalizeHex(
          parsed.brandIdentity?.primaryColor,
          "#2563EB",
        ),
        secondaryColor: normalizeHex(
          parsed.brandIdentity?.secondaryColor,
          "#0EA5E9",
        ),
      },
      designPrinciples:
        this.normalizeArray(parsed.designPrinciples, 8).length > 0
          ? this.normalizeArray(parsed.designPrinciples, 8)
          : [
              "Clarity first",
              "Fast to complete key task",
              "Accessible by default",
            ],
      coreDemographic:
        String(parsed.coreDemographic || "").trim() ||
        "Mobile-first users most likely to benefit from the app’s core value.",
      targetPlatforms,
      summary:
        String(parsed.summary || "").trim() ||
        `${data.appName} implementation expansion generated.`,
      appConstantsProfile,
      competitorScreenshotFeatures: screenshotFeatures,
      implementationStarterPrompt:
        implementationStarterPrompt ||
        this.buildStarterPromptFallback({
          appName: data.appName,
          summary:
            String(parsed.summary || "").trim() ||
            `${data.appName} implementation expansion generated.`,
          coreFeature,
          complementaryFeatures: complementary.slice(0, 5),
          screenshotFeatures,
          appConstantsProfile,
        }),
      firebaseConfigSnippet:
        String(data.firebaseConfigSnippet || "").trim() ||
        String(parsed.firebaseConfigSnippet || "").trim() ||
        "",
      coreBuilderTechStack: CORE_BUILDER_TECH_STACK,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      savedAt: serverTimestamp(),
    };

    await setDoc(doc(db, PLANS_COLLECTION, planId), payload);
    return {
      ...payload,
      createdAt: new Date(),
      updatedAt: new Date(),
      savedAt: new Date(),
    } as ImplementationPlan;
  }

  static async generateConfigFieldSuggestions(
    data: GenerateImplementationConfigFieldsDTO,
  ): Promise<Partial<AppConstantsInputProfile>> {
    const fallback = this.defaultAppConstantsProfile(data);
    const existing = this.normalizeAppConstantsProfile(data.existingProfile, fallback);
    const selected = (data.selectedFields || []).filter(Boolean);
    if (!selected.length) {
      return {};
    }

    const prompt = [
      "You are an app configuration strategist.",
      "Generate values only for the requested fields using the allowed enums.",
      "",
      "App context:",
      ...buildAppContextLines({
        name: data.appName,
        description: data.description,
        category: data.category,
        platform: data.platform,
        storeUrl: data.storeUrl,
        androidStoreUrl: data.androidStoreUrl,
        iosStoreUrl: data.iosStoreUrl,
        researchContext: data.researchContext,
      }).map((line) => `- ${line}`),
      "",
      `Current profile JSON: ${JSON.stringify(existing)}`,
      `Requested fields: ${selected.join(", ")}`,
      "",
      "Rules:",
      "- Return only valid JSON object.",
      "- Only include requested fields in response.",
      "- Respect allowed enums exactly.",
      "- Prefer practical production defaults.",
    ].join("\n");

    const response = await AIService.generateJSON<AIConfigFields>(prompt, {
      model: data.model || undefined,
      temperature: 0.1,
      maxTokens: 1200,
      structuredOutputName: "implementation_config_fields",
      structuredOutputSchema: APP_CONFIG_FIELD_SCHEMA,
    });

    const normalized = this.normalizeAppConstantsProfile(response, existing);
    return selected.reduce<Partial<AppConstantsInputProfile>>((acc, key) => {
      (acc as any)[key] = normalized[key];
      return acc;
    }, {});
  }

  static async analyzeCompetitorScreenshots(
    data: Pick<
      CreateImplementationPlanDTO,
      | "appName"
      | "description"
      | "category"
      | "platform"
      | "storeUrl"
      | "androidStoreUrl"
      | "iosStoreUrl"
      | "researchContext"
      | "model"
      | "competitorScreenshots"
    >,
  ): Promise<CompetitorScreenshotFeature[]> {
    const screenshots = Array.isArray(data.competitorScreenshots)
      ? data.competitorScreenshots
      : [];
    if (!screenshots.length) return [];

    const prompt = [
      "You are a mobile product analyst.",
      "Analyze each attached screenshot and extract concrete app feature implementation details.",
      "",
      "Target app context:",
      ...buildAppContextLines({
        name: data.appName,
        description: data.description,
        category: data.category,
        platform: data.platform,
        storeUrl: data.storeUrl,
        androidStoreUrl: data.androidStoreUrl,
        iosStoreUrl: data.iosStoreUrl,
        researchContext: data.researchContext,
      }).map((line) => `- ${line}`),
      "",
      "Rules:",
      "- Return one or more features per screenshot when possible.",
      "- Keep output concrete and implementation-focused.",
      "- screenshotLabel should map back to screenshot order/name.",
      "- Avoid generic feature names.",
      "- Return strict JSON only with key competitorScreenshotFeatures.",
    ].join("\n");
    const analysisModel = this.resolveStableScreenshotAnalysisModel(data.model);

    const batchSize = 2;
    const allFeatures: CompetitorScreenshotFeature[] = [];
    for (let index = 0; index < screenshots.length; index += batchSize) {
      const batch = screenshots.slice(index, index + batchSize);
      const inputImages = batch
        .map((item) => ({
          dataUrl: item.dataUrl,
          mimeType: item.mimeType,
        }))
        .filter((item) => item.dataUrl);

      let response:
        | { competitorScreenshotFeatures?: AIRawPlan["competitorScreenshotFeatures"] }
        | null = null;
      try {
        response = await AIService.generateJSON<{
          competitorScreenshotFeatures?: AIRawPlan["competitorScreenshotFeatures"];
        }>(prompt, {
          model: analysisModel || undefined,
          temperature: 0.2,
          maxTokens: 1600,
          structuredOutputName: "screenshot_feature_analysis",
          structuredOutputSchema: SCREENSHOT_FEATURE_SCHEMA,
          inputImages,
        });
      } catch {
        const fallbackText = await AIService.generateText(
          `${prompt}\n\nIMPORTANT: return valid JSON only.`,
          {
            model: analysisModel || "gemini-2.5-flash",
            temperature: 0.2,
            maxTokens: 1800,
            inputImages,
          },
        );
        response = this.parseJson<{
          competitorScreenshotFeatures?: AIRawPlan["competitorScreenshotFeatures"];
        }>(fallbackText.text);
      }

      allFeatures.push(
        ...this.normalizeCompetitorScreenshotFeatures(
          response?.competitorScreenshotFeatures,
        ),
      );
    }

    return allFeatures;
  }

  static async getUserPlans(
    userId: string,
    appId?: string,
  ): Promise<ImplementationPlan[]> {
    const constraints = [where("userId", "==", userId)];
    if (appId) constraints.push(where("appId", "==", appId));
    const q = query(collection(db, PLANS_COLLECTION), ...constraints);
    const snapshot = await getDocs(q);
    return snapshot.docs
      .map((item) => {
        const data = item.data() as any;
        const legacyComplementary = this.normalizeArray(
          data.complementaryFeatures,
          10,
        );
        const normalizedComplementary: FeatureDetail[] = Array.isArray(
          data.complementaryFeatures,
        )
          ? (data.complementaryFeatures as any[])
              .slice(0, 5)
              .map((feature: any, index) =>
                this.toFeatureDetail(
                  feature,
                  `Complementary Feature ${index + 1}`,
                  `Adds practical support for ${
                    data.appName || "the app"
                  }'s core value.`,
                ),
              )
          : legacyComplementary
              .slice(0, 5)
              .map((feature, index) =>
                this.toFeatureDetail(
                  feature,
                  `Complementary Feature ${index + 1}`,
                  `Adds practical support for ${
                    data.appName || "the app"
                  }'s core value.`,
                ),
              );

        return {
          ...data,
          coreFeature: this.toFeatureDetail(
            data.coreFeature,
            "Core Value Delivery",
            `The primary value loop that makes ${
              data.appName || "the app"
            } meaningfully useful.`,
          ),
          complementaryFeatures: normalizedComplementary,
          appConstantsProfile: data.appConstantsProfile
            ? this.normalizeAppConstantsProfile(
                data.appConstantsProfile,
                this.defaultAppConstantsProfile({
                  appId: data.appId || "",
                  appName: data.appName || "",
                  description: data.summary || "",
                  category: "productivity",
                  storeUrl: data.storeUrl || "",
                  androidStoreUrl: data.androidStoreUrl || "",
                  iosStoreUrl: data.iosStoreUrl || "",
                }),
              )
            : undefined,
          competitorScreenshotFeatures: this.normalizeCompetitorScreenshotFeatures(
            data.competitorScreenshotFeatures,
          ),
          implementationStarterPrompt: String(
            data.implementationStarterPrompt || "",
          ),
          firebaseConfigSnippet: String(data.firebaseConfigSnippet || ""),
          coreBuilderTechStack: String(data.coreBuilderTechStack || CORE_BUILDER_TECH_STACK),
          createdAt: this.toDate(data.createdAt),
          updatedAt: this.toDate(data.updatedAt),
          savedAt: data.savedAt ? this.toDate(data.savedAt) : undefined,
        } as ImplementationPlan;
      })
      .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
  }

  static async deletePlan(planId: string): Promise<void> {
    await deleteDoc(doc(db, PLANS_COLLECTION, planId));
  }

  static async savePlan(planId: string): Promise<void> {
    await updateDoc(doc(db, PLANS_COLLECTION, planId), {
      savedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  }

  static exportAsMarkdown(plan: ImplementationPlan): string {
    return `# Implementation Plan Generator Report: ${plan.appName}

## Summary
${plan.summary}

## Suggested Best App Name
${plan.suggestedBestAppName}

## Core Feature
### ${plan.coreFeature.name}
- Details: ${plan.coreFeature.details}
- How It Works: ${plan.coreFeature.howItWorks}
- Success Looks Like: ${plan.coreFeature.successLooksLike}

## 5 Complementary Features
${plan.complementaryFeatures
  .map(
    (feature, index) => `### ${index + 1}. ${feature.name}
- Details: ${feature.details}
- How It Works: ${feature.howItWorks}
- Success Looks Like: ${feature.successLooksLike}`,
  )
  .join("\n\n")}

## Brand Identity
- Primary Color: ${plan.brandIdentity.primaryColor}
- Secondary Color: ${plan.brandIdentity.secondaryColor}

## Design Principles
${plan.designPrinciples.map((item) => `- ${item}`).join("\n")}

## Core Demographic
${plan.coreDemographic}

## Target Platforms
${plan.targetPlatforms.map((item) => `- ${item.toUpperCase()}`).join("\n")}

## App Constants Profile
${plan.appConstantsProfile
  ? Object.entries(plan.appConstantsProfile)
      .map(([key, value]) => `- ${key}: ${String(value)}`)
      .join("\n")
  : "- N/A"}

## AI Provider Selection (Optional)
${plan.appConstantsProfile
  ? `- ai_text_generation: ${plan.appConstantsProfile.aiTextGeneration}
- ai_image_generation: ${plan.appConstantsProfile.aiImageGeneration}
- ai_video_generation: ${plan.appConstantsProfile.aiVideoGeneration}

\`\`\`json
${JSON.stringify(
  {
    aiProviders: {
      enabled: plan.appConstantsProfile.aiProvidersEnabled,
      textGeneration: plan.appConstantsProfile.aiTextGeneration,
      imageGeneration: plan.appConstantsProfile.aiImageGeneration,
      videoGeneration: plan.appConstantsProfile.aiVideoGeneration,
    },
  },
  null,
  2,
)}
\`\`\``
  : "N/A"}

## Competitor Screenshot Features
${(plan.competitorScreenshotFeatures || []).length
  ? (plan.competitorScreenshotFeatures || [])
      .map(
        (item, index) => `### ${index + 1}. ${item.screenshotLabel} - ${item.featureName}
- Details: ${item.featureDetails}
- Implementation Notes: ${item.implementationNotes}
- User Flow: ${item.userFlow}`,
      )
      .join("\n\n")
  : "No competitor screenshots analyzed."}

## Implementation Starter Prompt
${plan.implementationStarterPrompt || "N/A"}

## Firebase Config
This is firebase config of the project, use it where necessary.
${plan.firebaseConfigSnippet
  ? `\n\`\`\`ts\n${plan.firebaseConfigSnippet}\n\`\`\``
  : "\nN/A"}

## Core Builder Tech Stack
${plan.coreBuilderTechStack || CORE_BUILDER_TECH_STACK}
`.trim();
  }

  static exportAsJSON(plan: ImplementationPlan): string {
    return JSON.stringify(plan, null, 2);
  }
}
